import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FlaskConical } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import HomebrewContentCard from './HomebrewContentCard';
import { loadHomebrewContent, deleteHomebrewContent, duplicateHomebrew, exportHomebrew } from '../../services/homebrewStorageService';
import { registerComposedMechanic, removeComposedMechanic, computeRegistryKey } from '../../config/composedMechanicRegistry';
import MechanicComposer from './composer/MechanicComposer';
import ImportHomebrewModal from './ImportHomebrewModal';
import BrewmasterTab from './brewmaster/BrewmasterTab';

const SHELVES = [
  { type: 'mechanic', label: 'Abilities', color: 'text-yellow-400', statusFilter: 'ready', contextFilter: 'ability' },
  { type: 'mechanic', label: 'Domain Cards', color: 'text-purple-400', statusFilter: 'ready', contextFilter: 'domain-card' },
  { type: 'ancestry', label: 'Ancestries', color: 'text-green-400', statusFilter: 'ready' },
  { type: 'community', label: 'Communities', color: 'text-amber-300', statusFilter: 'ready' },
  { type: 'campaign-frame', label: 'Campaign Frames', color: 'text-red-300', statusFilter: 'ready' },
];

function HomebrewWorkshop() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [homebrewContent, setHomebrewContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editingMechanic, setEditingMechanic] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [unreviewedCount, setUnreviewedCount] = useState(0);

  // Persist Brewmaster visibility in URL so it survives reloads
  const showBrewmaster = searchParams.get('view') === 'brewmaster';
  const setShowBrewmaster = useCallback((show) => {
    if (show) {
      searchParams.set('view', 'brewmaster');
    } else {
      searchParams.delete('view');
    }
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadContent = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const content = await loadHomebrewContent(user.uid);
      setHomebrewContent(content);
      const unreviewed = content.filter(
        (item) => item.reviewStatus === 'auto' || item.reviewStatus === 'needsReview'
      ).length;
      setUnreviewedCount(unreviewed);
      // Register any ready mechanics into the composed mechanic registry
      const readyMechanics = content.filter(c => c.type === 'mechanic' && c.status === 'ready');
      readyMechanics.forEach(m => {
        const key = m.data?.meta?.registryKey || computeRegistryKey(m.data) || m.id;
        registerComposedMechanic(key, m.data);
      });
    } catch (err) {
      console.error('Failed to load homebrew content:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const refreshContent = useCallback(async () => {
    if (!user?.uid) return;
    try {
      const content = await loadHomebrewContent(user.uid);
      setHomebrewContent(content);
      const unreviewed = content.filter(
        (item) => item.reviewStatus === 'auto' || item.reviewStatus === 'needsReview'
      ).length;
      setUnreviewedCount(unreviewed);
      // Re-register ready mechanics (sync registry after save/delete/duplicate)
      const readyMechanics = content.filter(c => c.type === 'mechanic' && c.status === 'ready');
      readyMechanics.forEach(m => {
        const key = m.data?.meta?.registryKey || computeRegistryKey(m.data) || m.id;
        registerComposedMechanic(key, m.data);
      });
    } catch (err) {
      console.error('Failed to refresh homebrew content:', err);
    }
  }, [user?.uid]);

  const handleDelete = async (contentId) => {
    try {
      // Find the item to get its registry key before deleting
      const item = homebrewContent.find(c => c.id === contentId);
      await deleteHomebrewContent(user.uid, contentId);
      if (item?.data?.meta?.registryKey) {
        removeComposedMechanic(item.data.meta.registryKey);
      } else if (item?.data) {
        const key = computeRegistryKey(item.data);
        if (key) removeComposedMechanic(key);
      }
      toast.success('Content deleted');
      refreshContent();
    } catch (err) {
      console.error('Failed to delete homebrew content:', err);
      toast.error('Failed to delete content');
    }
  };

  const handleDuplicate = async (contentId) => {
    try {
      await duplicateHomebrew(user.uid, contentId);
      toast.success('Content duplicated');
      refreshContent();
    } catch (err) {
      console.error('Failed to duplicate homebrew content:', err);
      toast.error('Failed to duplicate content');
    }
  };

  const handleExport = (contentItem) => {
    exportHomebrew(contentItem);
    toast.success('Export downloaded');
  };

  const handleEdit = (contentItem) => {
    if (contentItem.type === 'mechanic') {
      setEditingMechanic(contentItem.data || contentItem);
      setShowComposer(true);
    }
  };

  // Group content into shelves, sorted alphabetically by name
  const getShelfItems = (type, statusFilter, contextFilter) =>
    homebrewContent
      .filter(c => c.type === type && c.status === statusFilter
        && (!contextFilter || c.data?.meta?.containerContext === contextFilter))
      .sort((a, b) => (a.meta?.name || '').localeCompare(b.meta?.name || '') || (a.id || '').localeCompare(b.id || ''));

  const drafts = homebrewContent
    .filter(c => c.status === 'draft')
    .sort((a, b) => {
      const aTime = a.meta?.updatedAt?.toMillis?.() || a.meta?.updatedAt || 0;
      const bTime = b.meta?.updatedAt?.toMillis?.() || b.meta?.updatedAt || 0;
      return bTime - aTime;
    });

  const hasAnyContent = homebrewContent.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-3"></div>
          <p className="text-gray-400">Loading your brews...</p>
        </div>
      </div>
    );
  }

  if (showComposer) {
    return (
      <div>
        {/* Header row — same position and style as Storeroom */}
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-3xl font-bold text-red-400 font-serif">Homebrew Workshop</h2>
        </div>
        <MechanicComposer
          onClose={() => { setShowComposer(false); setEditingMechanic(null); }}
          onRequestClose={() => { setShowComposer(false); setEditingMechanic(null); }}
          onSave={() => { setShowComposer(false); setEditingMechanic(null); refreshContent(); }}
          userId={user.uid}
          editMechanic={editingMechanic}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Brewmaster — always mounted, hidden when inactive to preserve conversation state */}
      <div className={showBrewmaster ? '' : 'hidden'}>
        <div className="mb-6 flex justify-between items-center">
          <h2 className="text-3xl font-bold text-red-400 font-serif">Homebrew Workshop</h2>
        </div>
        <BrewmasterTab
          userId={user.uid}
          onCharacterCreated={() => { setShowBrewmaster(false); refreshContent(); }}
        />
        <button
          onClick={() => setShowBrewmaster(false)}
          className="mt-4 text-sm text-gray-400 hover:text-gray-200 transition-colors"
        >
          Back to Storeroom
        </button>
      </div>

      <div className={showBrewmaster ? 'hidden' : ''}>
      {/* Header row — matches Characters/Campaigns pattern */}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-3xl font-bold text-red-400 font-serif">Homebrew Storeroom</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowBrewmaster(true)}
            className="px-4 py-3 bg-gray-800 text-gray-100 rounded-md hover:text-amber-400 hover:bg-amber-900/30 border border-gray-600 hover:border-amber-700 transition-all duration-200 font-medium relative"
          >
            Brewmaster
            {unreviewedCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs bg-amber-500 text-white rounded-full">
                {unreviewedCount}
              </span>
            )}
          </button>
<button
            onClick={() => setShowComposer(true)}
            className="px-6 py-3 bg-gray-800 text-gray-100 rounded-md hover:text-red-400 hover:bg-red-900/30 border border-gray-600 hover:border-red-700 transition-all duration-200 font-medium"
          >
            + New Brew
          </button>
        </div>
      </div>

      {!hasAnyContent ? (
        // Empty state — matches Characters empty state
        <div className="text-center py-12">
          <FlaskConical className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-100 mb-2">No homebrew yet</h3>
          <p className="text-gray-400">Create your first custom content to get started!</p>
        </div>
      ) : (
        <div>
          {/* Ready content shelves — grouped by type */}
          {SHELVES.map(({ type, label, color, statusFilter, contextFilter }) => {
            const items = getShelfItems(type, statusFilter, contextFilter);
            if (items.length === 0) return null;
            return (
              <div key={type} className="mb-8">
                <h3 className={`text-xl font-bold ${color} mb-4`}>{label}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map(item => (
                    <HomebrewContentCard
                      key={item.id}
                      content={item}
                      onDelete={handleDelete}
                      onDuplicate={handleDuplicate}
                      onExport={handleExport}
                      onEdit={handleEdit}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Drafts shelf — always last */}
          {drafts.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-400 mb-4">Drafts</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {drafts.map(item => (
                  <HomebrewContentCard
                    key={item.id}
                    content={item}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    onExport={handleExport}
                    onEdit={handleEdit}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showImportModal && (
        <ImportHomebrewModal
          userId={user.uid}
          onClose={() => setShowImportModal(false)}
          onImported={refreshContent}
        />
      )}
      </div>
    </div>
  );
}

export default HomebrewWorkshop;
